import React, { useState, useEffect } from 'react';
import axios from 'axios';
import useEmergenTTS from '../../hooks/useEmergenTTS';
import { useLanguage } from '../../contexts/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLLAPSE_KEY = 'dailyBriefCollapsed';

const DailyBriefing = ({ tasks = [], collapsible = false }) => {
  const [briefing, setBriefing] = useState(null);
  const [collapsed, setCollapsedState] = useState(() => collapsible && localStorage.getItem(COLLAPSE_KEY) !== 'false');
  const setCollapsed = (value) => {
    setCollapsedState(value);
    localStorage.setItem(COLLAPSE_KEY, String(value));
  };
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  // Language context for multi-language support
  const { t, getLanguageCode, getLanguageName } = useLanguage();

  // High-quality Google TTS for Daily Briefing
  const {
    speakConversation,
    isSpeaking,
    stopSpeaking
  } = useEmergenTTS();

  // Cleanup voice operations when component unmounts
  useEffect(() => {
    return () => {
      console.log('🔇 DailyBriefing: Stopping voice operations on cleanup');
      try {
        stopSpeaking();
        if (window.speechSynthesis) {
          window.speechSynthesis.cancel();
        }
      } catch (error) {
        console.log('Error stopping voice operations on cleanup:', error);
      }
    };
  }, [stopSpeaking]);

  // Helper function to get time of day
  const getTimeOfDay = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'morning';
    if (hour < 17) return 'afternoon';
    return 'evening';
  };

  const generateDailyBriefing = async (isRetry = false) => {
    if (!isRetry) {
      setRetryCount(0);
    }
    setIsLoading(true);
    setError(null);
    console.log('🔍 Daily Briefing: Starting generation...');
    
    try {
      const toLocalYMD = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      const today = toLocalYMD(new Date());
      
      // Calculate yesterday and tomorrow dates
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayStr = toLocalYMD(yesterday);
      
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      const tomorrowStr = toLocalYMD(tomorrow);
      
      console.log('🔍 Daily Briefing: Yesterday:', yesterdayStr, 'Today:', today, 'Tomorrow:', tomorrowStr);
      console.log('🔍 Daily Briefing: Fetching focused tasks for briefing');

      // Get ONLY relevant tasks for briefing (yesterday, today, tomorrow)
      const tasksResponse = await axios.get(`${API}/tasks/briefing-relevant`, {
        params: { 
          today,
          past_days: 1,  // Only yesterday
          future_days: 1 // Only tomorrow
        },
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        timeout: 15000
      });

      const relevantTasks = tasksResponse.data || [];
      console.log('🔍 Daily Briefing: Found', relevantTasks.length, 'relevant tasks from backend');
      
      // Log all task dates for debugging
      relevantTasks.forEach(task => {
        console.log(`  📋 Task: "${task.title}" - Due: ${task.due_date} - Completed: ${task.completed}`);
      });
      
      // Categorize tasks
      // 1. Tasks of today
      const tasksDueToday = relevantTasks.filter(task => {
        if (!task.due_date) return false;
        const taskDate = String(task.due_date).slice(0, 10);
        const isDueToday = taskDate === today && !task.completed;
        if (isDueToday) {
          console.log(`  ✅ Task due TODAY: "${task.title}" (${taskDate})`);
        }
        return isDueToday;
      });
      
      // 2. Overdue tasks from YESTERDAY only
      const overdueTasks = relevantTasks.filter(task => {
        if (!task.due_date || task.completed) return false;
        const taskDate = String(task.due_date).slice(0, 10);
        const isYesterday = taskDate === yesterdayStr;
        if (isYesterday) {
          console.log(`  ⚠️ Overdue from YESTERDAY: "${task.title}" (${taskDate})`);
        }
        return isYesterday;
      });
      
      // 3. High-priority tasks of TOMORROW only
      const tomorrowHighPriority = relevantTasks.filter(task => {
        if (!task.due_date || task.completed) {
          return false;
        }
        
        const taskDate = String(task.due_date).slice(0, 10);
        const isTomorrow = taskDate === tomorrowStr;
        const isHighPriority = task.priority === 'High';
        
        if (isTomorrow && isHighPriority) {
          console.log(`  🔥 High-priority TOMORROW: "${task.title}" (${taskDate})`);
        }
        
        return isTomorrow && isHighPriority;
      });
      
      console.log('🔍 Daily Briefing: Today:', tasksDueToday.length, 'Yesterday Overdue:', overdueTasks.length, 'Tomorrow High-Priority:', tomorrowHighPriority.length);

      // Focused briefing request with relevant tasks only
      const briefingRequest = {
        date: today,
        tasks_due_today: tasksDueToday.map(task => ({
          title: task.title,
          priority: task.priority,
          due_time: task.time,
          category: task.category
        })),
        overdue_tasks_yesterday: overdueTasks.map(task => ({
          title: task.title,
          priority: task.priority,
          due_date: task.due_date,
          category: task.category
        })),
        tomorrow_high_priority: tomorrowHighPriority.map(task => ({
          title: task.title,
          due_date: task.due_date,
          due_time: task.time,
          category: task.category
        })),
        language: getLanguageName ? getLanguageName() : 'English',
        language_code: getLanguageCode ? getLanguageCode() : 'en'
      };

      console.log('📤 Sending to AI:', JSON.stringify({
        today_count: briefingRequest.tasks_due_today.length,
        yesterday_overdue_count: briefingRequest.overdue_tasks_yesterday.length,
        tomorrow_high_priority_count: briefingRequest.tomorrow_high_priority.length,
        tomorrow_tasks: briefingRequest.tomorrow_high_priority
      }, null, 2));

      console.log('🔍 Daily Briefing: Sending request to backend...');
      const startTime = Date.now();
      
      const response = await axios.post(`${API}/ai/daily-briefing`, briefingRequest, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        timeout: 60000 // 60 second timeout for AI processing (generous for LLM responses)
      });

      const endTime = Date.now();
      console.log('✅ Daily Briefing: Backend responded in', (endTime - startTime) / 1000, 'seconds');

      if (response.data.success) {
        const briefingData = response.data.data;
        console.log('✅ Daily Briefing: Processing response data...');
        
        // Simplified transformation for better performance
        const timeOfDay = getTimeOfDay();
        let greetingKey = 'goodMorningBriefing';
        if (timeOfDay === 'afternoon') greetingKey = 'goodAfternoonBriefing';
        else if (timeOfDay === 'evening') greetingKey = 'goodEveningBriefing';
        
        const transformedBriefing = {
          // Prefer the AI-generated greeting if present; fall back to localised default.
          greeting: briefingData.greeting || t(greetingKey),
          summary: briefingData.bullets ? briefingData.bullets.join('. ') : 'No specific tasks or priorities identified for today.',
          bullets: briefingData.bullets || [],
          date: briefingData.date,
          action: briefingData.action,
          cta: briefingData.cta || 'Have a productive day!',
          // New advisor-style structured fields (may be empty for legacy responses).
          topPriority: briefingData.top_priority || null,
          quickWins: Array.isArray(briefingData.quick_wins) ? briefingData.quick_wins : [],
          skipToday: Array.isArray(briefingData.skip_today) ? briefingData.skip_today : [],
          prepareForTomorrow: Array.isArray(briefingData.prepare_for_tomorrow) ? briefingData.prepare_for_tomorrow : [],
          motivation: briefingData.motivation || '',
          tasksDueToday: tasksDueToday,
          totalTasks: tasksDueToday.length
        };
        
        setBriefing(transformedBriefing);
        console.log('✅ Daily Briefing: Generation completed successfully');
      } else {
        throw new Error(response.data.error || 'Briefing generation failed');
      }
    } catch (error) {
      console.error('❌ Daily Briefing Error Details:', {
        message: error.message,
        code: error.code,
        response: error.response?.data,
        status: error.response?.status,
        timeout: error.code === 'ECONNABORTED',
        fullError: error
      });
      
      let errorMessage = t('unableToGenerateBriefing') + ' ';
      if (error.code === 'ECONNABORTED') {
        if (error.message?.includes('10000ms') || error.message?.includes('30000ms')) {
          errorMessage += 'Tasks are taking too long to load. Please refresh and try again.';
          console.warn('🕐 Timeout occurred while fetching tasks - may need to optimize task query or clear cache.');
        } else {
          errorMessage += 'AI processing timed out. Please try again.';
          console.warn('🕐 Timeout occurred during AI processing - this may indicate a backend processing issue.');
        }
      } else if (error.response?.status === 500) {
        errorMessage += 'Server error occurred.';
        console.error('🔥 Server error details:', error.response.data);
      } else if (error.response?.status === 401) {
        errorMessage += 'Authentication error. Please refresh and try again.';
      } else if (error.response?.status >= 400 && error.response?.status < 500) {
        errorMessage += `Client error (${error.response?.status}). Please try again.`;
      } else if (error.message?.includes('Network Error')) {
        errorMessage += 'Network connection error. Please check your connection and try again.';
      } else {
        errorMessage += error.message || 'Please try again.';
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const retryBriefing = async () => {
    const newRetryCount = retryCount + 1;
    setRetryCount(newRetryCount);
    console.log(`🔄 Retrying daily briefing generation (attempt ${newRetryCount})`);
    await generateDailyBriefing(true);
  };

  const handleToggleMode = () => {
    // No longer needed - remove this function
  };

  const playVoiceBriefing = async () => {
    if (isSpeaking) {
      // If currently speaking, stop the voice
      stopSpeaking();
    } else if (briefing) {
      // If not speaking, start the voice with high-quality Google TTS
      const textToSpeak = briefing.greeting && briefing.bullets 
        ? `${briefing.greeting}. ${briefing.bullets.join('. ')}`
        : briefing.summary || formatBriefingText(briefing);
      
      console.log('🎤 Playing Daily Briefing with Google TTS:', textToSpeak.substring(0, 50) + '...');
      const langCode = getLanguageCode ? getLanguageCode() : 'en';
      console.log('🎤 DailyBriefing: Speaking in language code:', langCode);
      await speakConversation(textToSpeak, 'nova', langCode); // Use nova voice (young female) with language support
    }
  };

  const formatBriefingText = (briefingData) => {
    if (!briefingData) return '';
    
    let text = '';
    
    if (briefingData.greeting) {
      text += `${briefingData.greeting}\n\n`;
    }
    
    if (briefingData.summary) {
      text += `${briefingData.summary}\n\n`;
    }
    
    if (briefingData.priorities && briefingData.priorities.length > 0) {
      text += "🎯 Today's Priorities:\n";
      briefingData.priorities.forEach((task, index) => {
        text += `${index + 1}. ${task.title}`;
        if (task.due_time) text += ` at ${task.due_time}`;
        text += '\n';
      });
      text += '\n';
    }
    
    if (briefingData.overdue && briefingData.overdue.length > 0) {
      text += "⚠️ Overdue Tasks:\n";
      briefingData.overdue.forEach((task, index) => {
        text += `${index + 1}. ${task.title}`;
        if (task.due_date) text += ` (Due: ${new Date(task.due_date).toLocaleDateString()})`;
        text += '\n';
      });
      text += '\n';
    }
    
    if (briefingData.suggestions && briefingData.suggestions.length > 0) {
      text += "💡 Suggestions:\n";
      briefingData.suggestions.forEach((suggestion, index) => {
        text += `${index + 1}. ${suggestion}\n`;
      });
    }
    
    return text;
  };

  if (collapsible && collapsed) {
    const preview = briefing?.topPriority?.task_title
      ? `🎯 ${briefing.topPriority.task_title}`
      : briefing?.greeting || t('aiPoweredTaskSummary');
    return (
      <div
        className="flex items-center gap-3 rounded-xl border border-indigo-100 bg-gradient-to-r from-indigo-50 to-blue-50 px-4 py-2.5 mb-4"
        data-testid="daily-briefing-collapsed"
      >
        <span className="w-7 h-7 flex-shrink-0 rounded-lg bg-gradient-to-br from-indigo-500 to-blue-600 text-white flex items-center justify-center text-sm">📋</span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-gray-900 leading-tight">{t('dailyBriefing')}</p>
          <p className="text-xs text-gray-600 truncate" data-testid="daily-briefing-collapsed-preview">{preview}</p>
        </div>
        {!briefing && (
          <button
            onClick={() => { setCollapsed(false); generateDailyBriefing(); }}
            className="px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-xs font-medium hover:bg-indigo-700 transition-colors"
            data-testid="daily-briefing-collapsed-get"
          >
            {t('getBriefing')}
          </button>
        )}
        <button
          onClick={() => setCollapsed(false)}
          className="px-2 py-1.5 rounded-lg text-xs font-medium text-indigo-700 hover:bg-indigo-100 transition-colors"
          aria-label="Expand Daily Brief"
          data-testid="daily-briefing-expand"
        >
          Show ▾
        </button>
      </div>
    );
  }

  return (
    <div className={`bg-gradient-to-br from-indigo-50 to-blue-50 rounded-2xl shadow-sm ${collapsible ? 'p-4 mb-4' : 'p-6 mb-6'}`} data-testid="daily-briefing-expanded">
      {/* Header with Title and Dynamic Button on Same Line */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-blue-600 rounded-xl flex items-center justify-center">
            <span className="text-white text-xl">📋</span>
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900">{t('dailyBriefing')}</h2>
            <p className="text-sm text-gray-600">{t('aiPoweredTaskSummary')}</p>
          </div>
        </div>
        
        {/* Dynamic Button - Get Briefing OR Voice */}
        <div className="flex items-center gap-2">
          {!briefing ? (
            /* Show Get Briefing button when no briefing is displayed */
            <button
              onClick={generateDailyBriefing}
              disabled={isLoading}
              className={`px-6 py-3 rounded-lg font-medium transition-colors flex items-center space-x-2 ${
                isLoading
                  ? 'bg-gray-400 text-white cursor-not-allowed'
                  : 'bg-gradient-to-r from-indigo-600 to-blue-600 text-white hover:from-indigo-700 hover:to-blue-700'
              }`}
            >
              {isLoading && (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              )}
              <span>{isLoading ? `${t('loading')}...` : `🤖 ${t('getBriefing')}`}</span>
            </button>
          ) : (
            /* Show Voice button when briefing is displayed */
            <button
              onClick={playVoiceBriefing}
              className={`px-6 py-3 rounded-lg font-medium transition-colors flex items-center space-x-2 ${
                isSpeaking
                  ? 'bg-red-600 text-white hover:bg-red-700'
                  : 'bg-purple-600 text-white hover:bg-purple-700'
              }`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
              </svg>
              <span>{isSpeaking ? '⏹️ Stop Voice' : `🎤 ${t('voiceButton')}`}</span>
            </button>
          )}
          {collapsible && (
            <button
              onClick={() => setCollapsed(true)}
              className="px-2 py-1.5 rounded-lg text-xs font-medium text-indigo-700 hover:bg-indigo-100 transition-colors"
              aria-label="Collapse Daily Brief"
              data-testid="daily-briefing-collapse"
            >
              Hide ▴
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <p className="text-red-800 text-sm">{error}</p>
              {retryCount > 0 && (
                <p className="text-red-600 text-xs mt-1">Retry attempt {retryCount}</p>
              )}
            </div>
            <button
              onClick={retryBriefing}
              disabled={isLoading}
              className="ml-3 px-3 py-1 bg-red-100 text-red-800 rounded-md text-sm hover:bg-red-200 transition-colors disabled:opacity-50"
            >
              {isLoading ? 'Retrying...' : 'Retry'}
            </button>
          </div>
        </div>
      )}

      {/* Text Content - Always Displayed When Available */}
      {briefing && (
        <div className="bg-white rounded-xl p-5 shadow-sm" data-testid="daily-briefing-content">
          <div className="prose prose-sm max-w-none">
            {briefing.greeting && (
              <div className="mb-4">
                <h3 className="text-lg font-medium text-gray-900 mb-2">👋 {briefing.greeting}</h3>
              </div>
            )}

            {/* Advisor-style cards (new) */}
            {briefing.topPriority && briefing.topPriority.task_title && (
              <div
                data-testid="briefing-top-priority"
                className="mb-4 rounded-xl border border-red-200 bg-gradient-to-br from-red-50 to-orange-50 p-4"
              >
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-9 h-9 rounded-full bg-red-100 flex items-center justify-center">
                    <span className="text-red-700 text-lg">🎯</span>
                  </div>
                  <div className="flex-1">
                    <p className="text-xs font-semibold uppercase tracking-wide text-red-700 mb-1">
                      Top priority
                    </p>
                    <p className="text-base font-semibold text-gray-900 leading-snug">
                      {briefing.topPriority.task_title}
                      {briefing.topPriority.due_time && (
                        <span className="ml-2 text-sm font-normal text-gray-500">
                          · {briefing.topPriority.due_time}
                        </span>
                      )}
                    </p>
                    {briefing.topPriority.why && (
                      <p className="text-sm text-gray-700 mt-1">{briefing.topPriority.why}</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {briefing.quickWins && briefing.quickWins.length > 0 && (
              <div
                data-testid="briefing-quick-wins"
                className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50 p-4"
              >
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-emerald-700 text-lg">⚡</span>
                  <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">
                    Quick wins
                  </p>
                </div>
                <ul className="space-y-2">
                  {briefing.quickWins.map((qw, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <span className="text-emerald-600 mt-1">•</span>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{qw.task_title}</p>
                        {qw.why && <p className="text-xs text-gray-600 mt-0.5">{qw.why}</p>}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {briefing.skipToday && briefing.skipToday.length > 0 && (
              <div
                data-testid="briefing-skip-today"
                className="mb-4 rounded-xl border border-gray-200 bg-gray-50 p-4"
              >
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-gray-600 text-lg">⏭️</span>
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                    Skip today
                  </p>
                </div>
                <ul className="space-y-2">
                  {briefing.skipToday.map((sk, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <span className="text-gray-500 mt-1">•</span>
                      <div>
                        <p className="text-sm font-medium text-gray-800 line-through decoration-gray-400">
                          {sk.task_title}
                        </p>
                        {sk.why && <p className="text-xs text-gray-600 mt-0.5">{sk.why}</p>}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {briefing.prepareForTomorrow && briefing.prepareForTomorrow.length > 0 && (
              <div
                data-testid="briefing-prepare-tomorrow"
                className="mb-4 rounded-xl border border-indigo-200 bg-indigo-50 p-4"
              >
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-indigo-700 text-lg">📅</span>
                  <p className="text-xs font-semibold uppercase tracking-wide text-indigo-700">
                    Prepare for tomorrow
                  </p>
                </div>
                <ul className="space-y-1.5">
                  {briefing.prepareForTomorrow.map((pt, idx) => (
                    <li key={idx} className="flex items-baseline gap-2 text-sm">
                      <span className="text-indigo-500">→</span>
                      <span className="font-medium text-gray-900">{pt.task_title}</span>
                      {pt.due_time && (
                        <span className="text-xs text-gray-500">· {pt.due_time}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Fallback: when no structured advisor data was returned, show bullets like before */}
            {!briefing.topPriority &&
              (!briefing.quickWins || briefing.quickWins.length === 0) &&
              briefing.bullets && briefing.bullets.length > 0 && (
                <div className="mb-4">
                  <ul className="space-y-3">
                    {briefing.bullets.map((bullet, index) => (
                      <li key={index} className="flex items-start space-x-3">
                        <span className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-medium mt-0.5">
                          {index + 1}
                        </span>
                        <span className="flex-1 text-gray-700 leading-relaxed">{bullet}</span>
                      </li>
                    ))}
                  </ul>
                </div>
            )}

            {/* Closing motivation + CTA */}
            {(briefing.motivation || briefing.cta) && (
              <div className="mt-4 pt-4 border-t border-gray-200 space-y-1">
                {briefing.motivation && (
                  <p className="text-sm font-medium text-gray-800 italic">
                    {briefing.motivation}
                  </p>
                )}
                {briefing.cta && (
                  <p className="text-sm text-blue-700">
                    <span className="font-semibold">Next:</span> {briefing.cta}
                  </p>
                )}
              </div>
            )}

            {/* Empty state when no advisor data AND no bullets */}
            {!briefing.topPriority &&
              (!briefing.bullets || briefing.bullets.length === 0) &&
              (!briefing.quickWins || briefing.quickWins.length === 0) && (
                <div className="mb-4">
                  <p className="text-gray-700 leading-relaxed">
                    {briefing.summary || 'No specific tasks or priorities for today.'}
                  </p>
                </div>
              )}

            {briefing.totalTasks > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-600 text-center">
                  📊 Briefing covers all {briefing.totalTasks} tasks due today
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Speaking Indicator */}
      {isSpeaking && (
        <div className="mt-4 flex items-center justify-center space-x-2 text-purple-600">
          <div className="w-3 h-3 bg-purple-500 rounded-full animate-pulse"></div>
          <span className="text-sm font-medium">AI is speaking your briefing...</span>
        </div>
      )}

      {!briefing && !isLoading && (
        <div className="bg-white rounded-xl p-6 shadow-sm text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl">📋</span>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">{t('readyForDailyBriefing')}</h3>
          <p className="text-gray-600 text-sm mb-4">
            {t('getAiPoweredSummary')}
          </p>
          <p className="text-xs text-gray-500">
            {t('clickGetBriefingInstructions')}
          </p>
        </div>
      )}
    </div>
  );
};

export default DailyBriefing;